export type GameMode = 'modern' | 'classic'

export const GAME_MODE_KEY = 'star-snake-game-mode'
export const SNAKE_WIDTH_SCALE_KEY = 'star-snake-snake-width-scale'
export const CAMERA_ZOOM_SCALE_KEY = 'star-snake-camera-zoom-scale'
export const RENDER_QUALITY_KEY = 'star-snake-render-quality'

export function loadRenderQuality(): number {
  const stored = window.localStorage.getItem(RENDER_QUALITY_KEY)
  if (stored !== null) {
    const v = parseInt(stored, 10)
    if (v >= 0 && v <= 2) return v
  }
  return 1
}

const MOVEMENT_TUNING_SCHEMA_KEY = 'star-snake-movement-tuning-schema-version'
const MOVEMENT_TUNING_SCHEMA_VERSION = 6

const STALE_TUNING_KEYS = [
  'star-snake-turn-rate',
  'star-snake-base-speed',
  'star-snake-steering-response',
  'star-snake-cursor-turn-rate',
  'star-snake-prediction-blend',
  'star-snake-remote-blend',
  'star-snake-remote-extrapolation-ms',
  'star-snake-spark-pickup-radius',
  'star-snake-net-enter-margin',
  'star-snake-net-exit-margin',
  'star-snake-net-spine-rate',
  'star-snake-net-minimap-rate',
]

export interface GameModePreset {
  steeringResponse: number
  cursorTurnRate: number
  snakeWidthScale: number
  cameraZoomScale: number
}

export const GAME_MODE_PRESETS: Record<GameMode, GameModePreset> = {
  modern: {
    steeringResponse: 1.0,
    cursorTurnRate: 20,
    snakeWidthScale: 1.0,
    cameraZoomScale: 1.0,
  },
  classic: {
    steeringResponse: 1.0,
    cursorTurnRate: 14,
    snakeWidthScale: 1.35,
    cameraZoomScale: 1.2,
  },
}

export function sanitizeGameMode(value: string | null | undefined): GameMode {
  return value === 'classic' ? 'classic' : 'modern'
}

export function readGameMode(): GameMode {
  return sanitizeGameMode(window.localStorage.getItem(GAME_MODE_KEY))
}

function ensureVisualDefaults(mode: GameMode): void {
  if (window.localStorage.getItem(SNAKE_WIDTH_SCALE_KEY) === null) {
    window.localStorage.setItem(SNAKE_WIDTH_SCALE_KEY, String(GAME_MODE_PRESETS[mode].snakeWidthScale))
  }
  if (window.localStorage.getItem(CAMERA_ZOOM_SCALE_KEY) === null) {
    window.localStorage.setItem(CAMERA_ZOOM_SCALE_KEY, String(GAME_MODE_PRESETS[mode].cameraZoomScale))
  }
}

export function migrateMovementTuningDefaults(): void {
  const storedVersion = Number.parseInt(window.localStorage.getItem(MOVEMENT_TUNING_SCHEMA_KEY) ?? '0', 10)
  if (Number.isFinite(storedVersion) && storedVersion >= MOVEMENT_TUNING_SCHEMA_VERSION) {
    ensureVisualDefaults(readGameMode())
    return
  }

  for (const key of STALE_TUNING_KEYS) {
    window.localStorage.removeItem(key)
  }

  ensureVisualDefaults(readGameMode())
  window.localStorage.setItem(MOVEMENT_TUNING_SCHEMA_KEY, String(MOVEMENT_TUNING_SCHEMA_VERSION))
}
